// /staff/agent in the staff app's kit (CHANGE-070). Same tokens and calls as /partner/agent: make one (shown once),
// copy it, revoke one. Revoke asks first, because an agent using it stops at once.
import { useState } from 'react'
import { Button, Busy, Copy, Err, Fresh, Page, Section, Sources, Table, ago, errorText, useKit } from '../staff/kit'
import { when } from '../staff/common'
import { useAgentTokens } from './AgentAccessPage'

export function StaffAgentAccess() {
  const kit = useKit()
  const { list, make, revoke } = useAgentTokens()
  const [name, setName] = useState('')
  const [fresh, setFresh] = useState<string | null>(null)
  if (list.isLoading) return <Busy />
  if (list.error || !list.data) return <Page title="Agent access"><Err error={list.error} /></Page>
  const rows = list.data.rows
  const origin = window.location.origin

  const create = () => {
    if (!name.trim()) return
    make.mutate(name.trim(), {
      onSuccess: (r) => { setFresh(r.token); setName('') },
      onError: (e) => kit.toast(errorText(e), { bad: true }),
    })
  }
  const doRevoke = async (id: string, label: string, staff: boolean) => {
    const ok = await kit.confirm({
      title: `Revoke ${label}`, confirm: 'Revoke', danger: true,
      body: `Any agent using it stops at once${staff ? ', including its staff access' : ''}. This can't be undone.`,
    })
    if (ok.ok) revoke.mutate(id, { onSuccess: () => kit.toast(`Revoked ${label}`), onError: (e) => kit.toast(errorText(e), { bad: true }) })
  }

  return (
    <Page title="Agent access" testid="agent-access"
      sub={<Sources><Fresh at={list.dataUpdatedAt ? new Date(list.dataUpdatedAt) : null} label="Tokens" live /></Sources>}>
      <Section title="New token">
        <form className="st-filters" onSubmit={(e) => { e.preventDefault(); create() }}>
          <input className="st-input st-pay-note" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name, like Claude Code" maxLength={60} aria-label="Name" data-testid="agent-token-name" />
          <Button type="submit" tone="primary" disabled={make.isPending || !name.trim()} data-testid="agent-token-new">New token</Button>
        </form>
        {fresh ? (
          <div className="st-banner st-stack" data-testid="agent-token-fresh">
            <span className="st-strong">Copy it now. You won't see it again</span>
            <div className="st-row">
              <code className="st-mono st-agent-token" data-testid="agent-token-value">{fresh}</code>
              <Copy text={fresh} testid="agent-token-copy" />
            </div>
            <span className="st-muted">Tell your agent: "Read {origin}/agent.md. My token is above." Or save it in <span className="st-mono">~/.config/ernd/token.json</span></span>
          </div>
        ) : null}
        <p className="st-note">No token yet? Tell your agent to read {origin}/agent.md. It gives you a link to approve.</p>
      </Section>

      <Section title="Your tokens" count={rows.length}>
        <Table rows={rows} rowKey={(t) => t.id} empty="None yet."
          rowAttrs={() => ({ 'data-testid': 'agent-token-row' })} columns={[
            { key: 'name', head: 'Name', primary: true, cell: (t) => <>{t.client_name}{t.staff ? <span className="st-muted">, staff</span> : null}</> },
            { key: 'made', head: 'Made', nowrap: true, cell: (t) => when(t.approved_at) },
            { key: 'used', head: 'Last used', nowrap: true, cell: (t) => (t.last_used_at ? <span title={when(t.last_used_at)}>{ago(t.last_used_at)} ago</span> : 'Never') },
            { key: 'do', head: '', label: 'Do', cell: (t) => (
              <Button tone="danger" disabled={revoke.isPending} onClick={() => void doRevoke(t.id, t.client_name, t.staff)} data-testid="agent-token-revoke">Revoke</Button>
            ) },
          ]} />
      </Section>
    </Page>
  )
}
